import React, { useState, useEffect } from 'react';
import { getSettings, updateSettings, stopAllBots, startAllBots } from '../../api/adminApi';
import { useToast } from '../../context/ToastContext';
import LoadingData from '../../components/LoadingData';
import { theme } from '../../adminTheme';

export default function SettingsPage() {
  const toast = useToast();
  const [settings, setSettings] = useState(null);
  const [feePercentage, setFeePercentage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmBulk, setConfirmBulk] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getSettings();
        setSettings(res.data);
        setFeePercentage(res.data.default_fee_percentage);
      } catch { setError('Error al cargar configuración'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await updateSettings(feePercentage);
      setSettings(res.data);
      setSuccess('Configuración actualizada exitosamente');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleBulkToggle = async () => {
    if (!confirmBulk) return;
    try {
      const res = confirmBulk.active ? await startAllBots() : await stopAllBots();
      const r = res.data.result || {};
      if (confirmBulk.active) {
        toast.addToast(`Bots encendidos: ${r.started || r.restored || 0} iniciados`, 'success');
      } else {
        const errs = r.errors?.length || 0;
        toast.addToast(`Bots apagados: ${r.stopped || 0} detenidos${errs ? `, ${errs} errores` : ''}`, errs > 0 ? 'info' : 'success');
      }
    } catch (err) {
      toast.addToast(err.response?.data?.detail || 'Error al ejecutar acción', 'error');
    }
    setConfirmBulk(null);
  };

  if (loading) return <LoadingData />;

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Configuración Global</h1>

      <div style={styles.card}>
        <h3 style={{ color: theme.text, margin: '0 0 12px 0' }}>Fee por Defecto</h3>
        <form onSubmit={handleSave}>
          <div style={styles.field}>
            <label style={styles.label}>Porcentaje de Fee (%)</label>
            <p style={styles.hint}>
              Valor actual: <strong style={{ color: theme.accent }}>{settings?.default_fee_percentage ?? 10}%</strong>
            </p>
            <input
              type="number" step="0.1" min="0" max="50"
              value={feePercentage}
              onChange={(e) => setFeePercentage(parseFloat(e.target.value) || 0)}
              style={styles.input} required
            />
          </div>
          {error && <p style={{ color: theme.error }}>{error}</p>}
          {success && <p style={{ color: theme.success }}>{success}</p>}
          <button type="submit" style={styles.button} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </form>
        {settings?.updated_by && (
          <p style={{ color: theme.textMuted, fontSize: '0.8rem', marginTop: 16 }}>
            Última actualización por {settings.updated_by}
            {settings.updated_at ? ` el ${new Date(settings.updated_at).toLocaleString()}` : ''}
          </p>
        )}
      </div>

      <div style={{ ...styles.card, marginTop: 20 }}>
        <h3 style={{ color: theme.text, margin: '0 0 8px 0' }}>Control de Bots</h3>
        <p style={styles.hint}>Apagar o encender todos los bots simultáneamente.</p>
        <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
          <button onClick={() => setConfirmBulk({ active: false })} style={styles.dangerBtn}>
            Apagar todos
          </button>
          <button onClick={() => setConfirmBulk({ active: true })} style={styles.button}>
            Encender todos
          </button>
        </div>
      </div>

      {confirmBulk && (
        <div style={styles.modalOverlay} onClick={() => setConfirmBulk(null)}>
          <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: theme.text, marginTop: 0 }}>
              {confirmBulk.active ? 'Encender' : 'Apagar'} todos los bots
            </h3>
            <p style={{ color: theme.textMuted }}>
              ¿Estás seguro de que deseas <strong style={{ color: theme.text }}>{confirmBulk.active ? 'encender' : 'apagar'}</strong> TODOS los bots?
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={handleBulkToggle} style={confirmBulk.active ? styles.button : styles.dangerBtn}>
                Sí, {confirmBulk.active ? 'encender' : 'apagar'} todos
              </button>
              <button onClick={() => setConfirmBulk(null)} style={styles.cancelBtn}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '24px', maxWidth: '600px', minHeight: '100vh' },
  title: { margin: '0 0 24px 0', fontSize: '1.5rem', color: theme.text },
  card: { backgroundColor: theme.card, padding: '24px', borderRadius: '8px', border: `1px solid ${theme.border}` },
  field: { marginBottom: '16px' },
  label: { fontWeight: 500, display: 'block', marginBottom: 8, color: theme.text },
  hint: { fontSize: '0.85rem', color: theme.textMuted, margin: '0 0 8px 0' },
  input: { width: '100%', padding: '10px 12px', border: `1px solid ${theme.inputBorder}`, borderRadius: '6px', fontSize: '1rem', backgroundColor: theme.input, color: theme.text, boxSizing: 'border-box' },
  button: { padding: '10px 24px', backgroundColor: theme.accent, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '1rem', cursor: 'pointer' },
  dangerBtn: { padding: '10px 24px', backgroundColor: theme.error, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '1rem', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  confirmModal: { backgroundColor: theme.card, padding: '24px', borderRadius: '12px', border: `1px solid ${theme.border}`, width: '90%', maxWidth: '400px', textAlign: 'center' },
  cancelBtn: { flex: 1, padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: 'pointer', color: theme.text },
};
